/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */
import { call } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  Program,
  Stream,
  LicenseOptions,
  PlaybackLicenseData,
  CatchupDataAdapter,
  PaddingMode,
  ProgramTimes,
  TimeUtils,
  PlaybackLicenseResult,
  PlaybackContextApi,
  PlaybackAssetType,
  PlaybackContextType,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import AuthNUtils from './authNUtils';
import { getCatchupPaddingMode, getStartAndEndTimes } from '../epg/padding';

function* getPlaybackData(asset: Program, stream: Stream, options?: LicenseOptions): SagaIterator<PlaybackLicenseData> {
  let adapter: CatchupDataAdapter | undefined;
  let context: PlaybackContextType | undefined;

  const paddingMode: PaddingMode = yield call(getCatchupPaddingMode);
  if (options?.applyPadding && paddingMode === PaddingMode.GET_PLAYBACK_CONTEXT) {
    const programTimes: ProgramTimes = yield call(getStartAndEndTimes, asset);
    adapter = {
      manifestStartTime: programTimes.start,
      manifestEndTime: programTimes.end,
    };
    // if metadata end time+end padding > Current time (now), the playback type is start-over [TF28.090]
    if (TimeUtils.isAfterNow(programTimes.end)) {
      context = PlaybackContextType.START_OVER;
    }
  } else if (options?.streamStartTime && options?.streamEndTime) {
    adapter = {
      manifestStartTime: TimeUtils.toIsoStringSeconds(options.streamStartTime),
      manifestEndTime: TimeUtils.toIsoStringSeconds(options.streamEndTime),
    };
  }

  const result: PlaybackLicenseResult = yield Ducks.Api.callApi(
    PlaybackContextApi.getPlaybackData,
    asset.id,
    PlaybackAssetType.EPG,
    stream.id,
    adapter,
    context
  );

  if (!result.clearContent) {
    // update the token with the authN
    result.data.token = yield call(AuthNUtils.getAuthN);
  }

  return result.data;
}

function* getPlaybackLicenseLink(
  asset: Program,
  stream: Stream,
  options?: LicenseOptions
): SagaIterator<PlaybackLicenseResult> {
  let adapter: CatchupDataAdapter | undefined;
  let context: PlaybackContextType | undefined;

  const paddingMode: PaddingMode = yield call(getCatchupPaddingMode);
  if (options?.applyPadding && paddingMode === PaddingMode.GET_PLAYBACK_CONTEXT) {
    const programTimes: ProgramTimes = yield call(getStartAndEndTimes, asset);
    adapter = {
      manifestStartTime: programTimes.start,
      manifestEndTime: programTimes.end,
    };
    // if metadata end time+end padding > Current time (now), the playback type is start-over [TF28.090]
    if (TimeUtils.isAfterNow(programTimes.end)) {
      context = PlaybackContextType.START_OVER;
    }
  } else if (options?.streamStartTime && options?.streamEndTime) {
    adapter = {
      manifestStartTime: TimeUtils.toIsoStringSeconds(options.streamStartTime),
      manifestEndTime: TimeUtils.toIsoStringSeconds(options.streamEndTime),
    };
  }

  return yield Ducks.Api.callApi(
    PlaybackContextApi.getPlaybackUrl,
    asset.id,
    PlaybackAssetType.EPG,
    stream.id,
    adapter,
    context
  );
}

export default {
  getPlaybackData,
  getPlaybackLicenseLink,
};
